import { useQuery } from '@tanstack/react-query';
import { Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { api, type WidgetRow, type WidgetType } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

/**
 * Read-only обзор всех Kommo Dashboard виджетов, которые раздаёт Bridge.
 * Показывает что подключено, активно ли, и проходит ли health-check (живой
 * запрос к .json-эндпойнту виджета). Создание/редактирование — через seed-CLI,
 * здесь только мониторинг.
 */

const TYPE_LABEL: Record<WidgetType, string> = {
  'gross-fee': 'Gross Fee',
  'active-leads': 'Active Leads',
  'user-table': 'By Agent',
  calendar: 'Calendar',
  'calendar-table': 'Calendar Table',
};

function HealthBadge({ w }: { w: WidgetRow }): JSX.Element {
  if (!w.is_active) return <Badge variant="secondary">отключён</Badge>;
  const h = w.health;
  if (!h) return <Badge variant="outline">—</Badge>;
  if (h.status === 'inactive') return <Badge variant="secondary">отключён</Badge>;
  if (h.status === 'ok') {
    return (
      <Badge variant="success" title={`HTTP ${h.http ?? ''} · ${h.latency_ms} ms`}>
        OK · {h.latency_ms} ms
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" title={h.error ?? 'error'}>
      Ошибка{h.http ? ` (${h.http})` : ''}
    </Badge>
  );
}

export function Widgets(): JSX.Element {
  const { data = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['widgets'],
    queryFn: () => api.get<WidgetRow[]>('/admin/widgets'),
    // health-check ходит в Kommo/GCal → держим данные дольше, не дёргаем зря.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const copy = (url: string): void => {
    void navigator.clipboard.writeText(url);
  };

  const total = data.length;
  const broken = data.filter((w) => w.is_active && w.health?.status === 'error').length;
  const active = data.filter((w) => w.is_active).length;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Виджеты Kommo Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Все iframe-виджеты, которые Bridge отдаёт в дашборды Kommo. Health-check —
            живой запрос к каждому виджету.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Проверить
        </Button>
      </header>

      {!isLoading && total > 0 && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>Всего: <b className="text-foreground">{total}</b></span>
          <span>Активных: <b className="text-foreground">{active}</b></span>
          <span>
            С ошибкой:{' '}
            <b className={broken > 0 ? 'text-destructive' : 'text-foreground'}>{broken}</b>
          </span>
        </div>
      )}

      {isLoading ? (
        <TableSkeleton columns={['Тип', 'Название', 'Аккаунт', 'Детали', 'Состояние', 'Ссылка']} />
      ) : total === 0 ? (
        <p className="text-sm text-muted-foreground">Виджеты не настроены.</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип</TableHead>
                <TableHead>Название</TableHead>
                <TableHead>Аккаунт</TableHead>
                <TableHead>Детали</TableHead>
                <TableHead>Состояние</TableHead>
                <TableHead className="text-right">Ссылка</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((w) => (
                <TableRow key={`${w.type}-${w.id}`}>
                  <TableCell>
                    <Badge variant="outline">{TYPE_LABEL[w.type] ?? w.type}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{w.title}</TableCell>
                  <TableCell className="text-muted-foreground">{w.account_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {w.detail ?? '—'}
                  </TableCell>
                  <TableCell>
                    <HealthBadge w={w} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Копировать URL виджета"
                        onClick={() => copy(w.html_url)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <a href={w.html_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" title="Открыть виджет">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
